import { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { api } from '../lib/api';
import { getTomRouteLabel } from '../lib/tomRouteContext';

export interface TomNavigationContext {
  pathname: string;
  search: string;
  routeLabel: string;
}

interface ScreenData {
  type: string;
  data: any;
  timestamp: string;
  navigation?: TomNavigationContext;
}

interface DashboardData {
  stats: {
    totalStudents: number;
    activeSessions: number;
    buildingBlocks: number;
    learningPlans: number;
    totalTasks: number;
    totalEvidence: number;
  };
  todaySessions: Array<{
    SessionID: number;
    Lehrer: string;
    Fach: string;
    studentCount: number;
  }>;
}

interface StudentsData {
  selectedStudent: {
    Name: string;
    Vorname: string;
    Klasse: string;
    sophomorixUnid: string;
  } | null;
  studentList: Array<{
    Name: string;
    Vorname: string;
    Klasse: string;
  }>;
  activeTab: 'properties' | 'performance';
  kiAssessment: any;
  performanceData: Array<{
    Fach: string;
    Baustein: string;
    Lernplan: string;
    Aufgabe: string;
    Lehrer: string;
    Datum: string;
  }>;
}

interface LernbueroData {
  selectedSession: {
    SessionID: number;
    Lehrer: string;
    Fach: string;
    studentCount: number;
  } | null;
  attendanceData: Array<{
    Name: string;
    Vorname: string;
    Klasse: string;
    Anzahl: number;
  }>;
  panelType: 'A' | 'B';
  sessionInfo: any;
}

interface AttendanceData {
  students: Array<{
    Name: string;
    Vorname: string;
    Status: string;
    Klasse: string;
  }>;
  summary: {
    anwesend: number;
    krank: number;
    lernbuero: number;
  };
}

interface BuildingBlocksData {
  selectedBaustein: {
    BID: number;
    NAME: string;
    FACH: string;
    STUFE: string;
    JAHR: number;
  } | null;
  lernplaene: Array<{
    LPID: number;
    NAME: string;
    STATUS: string;
  }>;
  tasks: Array<{
    AID: number;
    NAME: string;
    STATUS: string;
  }>;
}

interface DeviceManagerData {
  devices: Array<{
    id: number;
    name: string;
    status: string;
    lastSeen: string;
  }>;
  connectionStatus: string;
}

interface SettingsData {
  userSettings: {
    username: string;
    preferences: any;
  };
  systemSettings: {
    version: string;
    lastUpdate: string;
  };
}

// Screen Data Collector Functions
const getDashboardScreenData = async (): Promise<DashboardData> => {
  try {
    const [studentCount, todaySessions, buildingBlockCount, learningPlanCount, taskCount, evidenceCount] = await Promise.all([
      api.get('/students/count').then(res => res.data.data?.total ?? 0),
      api.get('/lernbuero/sessions/today').then(res => res.data.data),
      api.get('/building-blocks/count').then(res => res.data.data?.total ?? 0),
      api.get('/learning-plans/count').then(res => res.data.data?.total ?? 0),
      api.get('/tasks/count').then(res => res.data.data?.total ?? 0),
      api.get('/evidence/count').then(res => res.data.data?.total ?? 0),
    ]);

    return {
      stats: {
        totalStudents: studentCount,
        activeSessions: todaySessions.length,
        buildingBlocks: buildingBlockCount,
        learningPlans: learningPlanCount,
        totalTasks: taskCount,
        totalEvidence: evidenceCount,
      },
      todaySessions: todaySessions.slice(0, 5), // Top 5 sessions
    };
  } catch (error) {
    console.error('Error fetching dashboard data:', error);
    return {
      stats: {
        totalStudents: 0,
        activeSessions: 0,
        buildingBlocks: 0,
        learningPlans: 0,
        totalTasks: 0,
        totalEvidence: 0,
      },
      todaySessions: [],
    };
  }
};

const getStudentsScreenData = (): StudentsData => {
  // Get data from sessionStorage (where the Students page stores its state)
  const selectedStudentStr = sessionStorage.getItem('students-selected-student');
  const selectedStudent = selectedStudentStr ? JSON.parse(selectedStudentStr) : null;
  const activeTab = sessionStorage.getItem('students-active-tab') as 'properties' | 'performance' || 'properties';
  
  console.log('📊 Students Screen Data:', {
    selectedStudentStr,
    selectedStudent,
    activeTab
  });
  
  return {
    selectedStudent,
    studentList: [], // This would need to be fetched from the Students component
    activeTab,
    kiAssessment: null, // This would need to be fetched from the Students component
    performanceData: [], // This would need to be fetched from the Students component
  };
};

const getLernbueroScreenData = (): LernbueroData => {
  // Get data from sessionStorage (where the Lernbuero page stores its state)
  const selectedSessionId = sessionStorage.getItem('lernbuero-selected-session-id');
  const panelType = sessionStorage.getItem('lernbuero-panel-type') as 'A' | 'B' || 'A';
  
  return {
    selectedSession: selectedSessionId ? { SessionID: parseInt(selectedSessionId), Lehrer: '', Fach: '', studentCount: 0 } : null,
    attendanceData: [],
    panelType,
    sessionInfo: null,
  };
};

const getAttendanceScreenData = (): AttendanceData => {
  // This would need to be implemented based on the Attendance page structure
  return {
    students: [],
    summary: {
      anwesend: 0,
      krank: 0,
      lernbuero: 0,
    },
  };
};

const getBuildingBlocksScreenData = (): BuildingBlocksData => {
  // This would need to be implemented based on the BuildingBlocks page structure
  return {
    selectedBaustein: null,
    lernplaene: [],
    tasks: [],
  };
};

const getDeviceManagerScreenData = (): DeviceManagerData => {
  // This would need to be implemented based on the DeviceManager page structure
  return {
    devices: [],
    connectionStatus: 'unknown',
  };
};

const getSettingsScreenData = (): SettingsData => {
  // This would need to be implemented based on the Settings page structure
  return {
    userSettings: {
      username: 'unknown',
      preferences: {},
    },
    systemSettings: {
      version: '1.5.0',
      lastUpdate: new Date().toISOString(),
    },
  };
};

function attachNavigation(
  pathname: string,
  search: string,
  partial: Omit<ScreenData, 'navigation'>
): ScreenData {
  return {
    ...partial,
    navigation: {
      pathname,
      search: search || '',
      routeLabel: getTomRouteLabel(pathname),
    },
  };
}

// Main Screen Data Collector
const collectScreenData = async (pathname: string, search: string): Promise<ScreenData> => {
  const timestamp = new Date().toISOString();
  
  switch (pathname) {
    case '/dashboard':
      return attachNavigation(pathname, search, {
        type: 'dashboard',
        data: await getDashboardScreenData(),
        timestamp,
      });
    case '/students':
      return attachNavigation(pathname, search, {
        type: 'students',
        data: getStudentsScreenData(),
        timestamp,
      });
    case '/lernbuero':
      return attachNavigation(pathname, search, {
        type: 'lernbuero',
        data: getLernbueroScreenData(),
        timestamp,
      });
    case '/attendance':
      return attachNavigation(pathname, search, {
        type: 'attendance',
        data: getAttendanceScreenData(),
        timestamp,
      });
    case '/building-blocks':
      return attachNavigation(pathname, search, {
        type: 'building-blocks',
        data: getBuildingBlocksScreenData(),
        timestamp,
      });
    case '/device-manager':
      return attachNavigation(pathname, search, {
        type: 'device-manager',
        data: getDeviceManagerScreenData(),
        timestamp,
      });
    case '/settings':
      return attachNavigation(pathname, search, {
        type: 'settings',
        data: getSettingsScreenData(),
        timestamp,
      });
    default:
      return attachNavigation(pathname, search, {
        type: 'unknown',
        data: { routePath: pathname },
        timestamp,
      });
  }
};

// Main Hook
export const useScreenData = () => {
  const location = useLocation();
  const [screenData, setScreenData] = useState<ScreenData | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchScreenData = async () => {
      setIsLoading(true);
      setError(null);
      
      try {
        const data = await collectScreenData(location.pathname, location.search);
        setScreenData(data);
        console.log('📊 Screen Data collected:', data);
      } catch (err) {
        console.error('❌ Error collecting screen data:', err);
        setError(err instanceof Error ? err.message : 'Unknown error');
      } finally {
        setIsLoading(false);
      }
    };

    fetchScreenData();
  }, [location.pathname, location.search]);

  // Listen for sessionStorage changes to update screen data in real-time
  useEffect(() => {
    const handleStorageChange = () => {
      if (location.pathname === '/students' || location.pathname === '/lernbuero') {
        const fetchScreenData = async () => {
          try {
            const data = await collectScreenData(location.pathname, location.search);
            setScreenData(data);
            console.log('📊 Screen Data updated from storage change:', data);
          } catch (err) {
            console.error('❌ Error updating screen data:', err);
          }
        };
        fetchScreenData();
      }
    };

    // Listen for storage events (from other tabs/windows)
    window.addEventListener('storage', handleStorageChange);
    
    // Also listen for custom events (from same tab)
    window.addEventListener('screenDataUpdate', handleStorageChange);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('screenDataUpdate', handleStorageChange);
    };
  }, [location.pathname, location.search]);

  return {
    screenData,
    isLoading,
    error,
    refresh: () => {
      const fetchScreenData = async () => {
        setIsLoading(true);
        setError(null);
        
        try {
          const data = await collectScreenData(location.pathname, location.search);
          setScreenData(data);
        } catch (err) {
          console.error('❌ Error refreshing screen data:', err);
          setError(err instanceof Error ? err.message : 'Unknown error');
        } finally {
          setIsLoading(false);
        }
      };
      
      fetchScreenData();
    },
  };
};

export default useScreenData;
